import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, Select, DatePicker } from 'antd';
import DrawerModal from '../../shared/DrawerModal';
import styles from './AddLicenseModal.styles';
import {
  closeModal,
  getIsVisibleModal,
} from '../../../redux/store/ui/common/index';
import {
  selectSelectedUser,
  updateUser,
  selectRejectionReasons,
  getRejectionReasons,
  rejectUserLicense,
} from '../../../redux/store/userData/users';
import { useIntl } from 'react-intl';
import { zIndex } from '../../../theme/theme';
import dayjs from 'dayjs';
import { ANT_PICKERS_FORMAT, formatAPIDate } from '../../../utils';
import { selectAdminBrands } from '../../../redux/store/userData/user';

const { Option } = Select;
const idStatuses = ['open', 'listed', 'verified', 'paused', 'deleted'];

export const MODAL_EDIT_USER_LICENSE = 'EditLicenseModal';

const EditLicenseModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const selectedUser = useSelector(selectSelectedUser);
  const visibleModal = useSelector(getIsVisibleModal(MODAL_EDIT_USER_LICENSE));
  const rejectionReasons = useSelector(selectRejectionReasons);
  const adminBrands = useSelector(selectAdminBrands);

  const selectedUserBrand = adminBrands?.filter(
    (brand) => brand.id === selectedUser?.brandId
  );

  const supportedLicenseCountries =
    selectedUserBrand[0]?.allowedDrivingLicenseCountries?.map((country) =>
      country.toUpperCase()
    );

  const license = selectedUser?.licenses?.[0];
  const [firstName, setFirstName] = useState(license?.firstName || '');
  const [lastName, setLastName] = useState(license?.lastName || '');
  const [number, setNumber] = useState(license?.number || '');
  const [classBValidFrom, setClassBValidFrom] = useState(
    license?.classB?.from || ''
  ) as any;
  const [classBValidUntil, setClassBValidUntil] = useState(
    license?.classB?.to || ''
  );
  const [issueDate, setIssueDate] = useState(license?.interval?.from || '');
  const [issuingAuthority, setIssuingAuthority] = useState(
    license?.issuingAuthority || ''
  );
  const [birthPlace, setBirthPlace] = useState(license?.birthPlace || '');
  const [dateOfBirth, setDateOfBirth] = useState(license?.dateOfBirth || '');

  const [issueCountry, setIssueCountry] = useState(license?.issueCountry || '');
  const [status, setStatus] = useState(license?.licenseStatus || 'listed');
  const [rejectedReason, setRejectedReason] = useState(
    license?.rejectedReason || ''
  );
  const dateFormat = 'YYYY-MM-DD';

  const handleOnSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();

    if (status === 'deleted') {
      dispatch(rejectUserLicense(selectedUser.id, rejectedReason));
      dispatch(closeModal(MODAL_EDIT_USER_LICENSE));
      return;
    }

    dispatch(
      updateUser(selectedUser.id, {
        license: {
          firstName,
          lastName,
          number,
          issueDate: formatAPIDate(issueDate),
          issuingAuthority,
          birthPlace,
          dateOfBirth: formatAPIDate(dateOfBirth),
          issueCountry,
          classBValidFrom: formatAPIDate(classBValidFrom),
          ...(classBValidUntil && {
            classBValidUntil: formatAPIDate(classBValidUntil),
          }),
          status,
        },
      })
    );
    dispatch(closeModal(MODAL_EDIT_USER_LICENSE));
  };

  const handleOnCancel = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    dispatch(closeModal(MODAL_EDIT_USER_LICENSE));
  };

  useEffect(() => {
    if (visibleModal) {
      const firstLicense = selectedUser?.licenses[0];

      setFirstName(firstLicense?.firstName || '');
      setLastName(firstLicense?.lastName || '');
      setNumber(firstLicense?.number || '');
      setIssueDate(firstLicense?.interval?.from || '');
      setClassBValidFrom(firstLicense?.classB?.from || '');
      setClassBValidUntil(firstLicense?.classB?.to || '');
      setIssuingAuthority(firstLicense?.issuingAuthority || '');
      setBirthPlace(firstLicense?.birthPlace || '');
      setDateOfBirth(firstLicense?.dateOfBirth || '');
      setIssueCountry(firstLicense?.issueCountry || '');
      setStatus(firstLicense?.licenseStatus || 'listed');
      setRejectedReason(firstLicense?.rejectedReason || '');

      if (rejectionReasons?.length === 0 || rejectionReasons === null) {
        dispatch(getRejectionReasons());
      }
    }
  }, [visibleModal]);

  return (
    <>
      <DrawerModal
        visible={visibleModal}
        onClose={() => {
          dispatch(closeModal('UserContactModal'));
        }}
      >
        <div className="update-phone-modal">
          <div className="phone-modal-wrapper">
            <div className="update-phone-modal-inputs">
              <p className="add-phone-title">
                {intl.formatMessage({
                  id: `fleetadmin.users.info.license.manualForm.edit.title`,
                })}
              </p>
              <div className="license-input">
                <p className="label">
                  {intl.formatMessage({
                    id: `fleetadmin.users.info.dl.status`,
                  })}
                </p>
                <Select
                  dropdownStyle={{ zIndex: zIndex.dropDown }}
                  value={status}
                  style={{ width: '100%' }}
                  placeholder={intl.formatMessage({
                    id: `fleetadmin.users.info.dl.status`,
                  })}
                  onChange={setStatus}
                >
                  {idStatuses.map((status) => (
                    <Option key={`area-option-${status}`} value={status}>
                      {intl.formatMessage({
                        id: `${'fleetadmin.status.'}${status}`,
                      })}
                    </Option>
                  ))}
                </Select>
              </div>
              {status === 'deleted' && (
                <div className="license-input">
                  <p className="label">
                    {intl.formatMessage({
                      id: `fleetadmin.users.info.dl.rejectedReason`,
                    })}
                  </p>
                  <Select
                    dropdownStyle={{ zIndex: zIndex.dropDown }}
                    value={rejectedReason}
                    style={{ width: '100%' }}
                    placeholder={intl.formatMessage({
                      id: `fleetadmin.users.info.dl.rejectionReason`,
                    })}
                    onChange={setRejectedReason}
                  >
                    {rejectionReasons?.map((reason) => (
                      <Option key={`area-option-${reason}`} value={reason}>
                        {intl.formatMessage({
                          id: `${'fleetadmin.reason.'}${reason}`,
                        })}
                      </Option>
                    ))}
                  </Select>
                </div>
              )}
              <div className="license-input">
                <p className="label">
                  {intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.firstName`,
                  })}
                </p>
                <Input
                  value={firstName}
                  placeholder={intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.firstName.placeholder`,
                  })}
                  onChange={(e: any) => {
                    setFirstName(e.target.value);
                  }}
                />
              </div>
              <div className="license-input">
                <p className="label">
                  {intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.lastName`,
                  })}
                </p>
                <Input
                  value={lastName}
                  placeholder={intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.lastName.placeholder`,
                  })}
                  onChange={(e: any) => {
                    setLastName(e.target.value);
                  }}
                />
              </div>
              <div className="license-input">
                <p className="label">
                  {intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.licenseNumber`,
                  })}
                </p>
                <Input
                  value={number}
                  placeholder={intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.licenseNumber.placeholder`,
                  })}
                  onChange={(e: any) => {
                    setNumber(e.target.value);
                  }}
                />
              </div>

              <div className="license-input">
                <p className="label">
                  {intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.birthPlace`,
                  })}
                </p>
                <Input
                  value={birthPlace}
                  placeholder={intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.birthPlace.placeholder`,
                  })}
                  onChange={(e: any) => {
                    setBirthPlace(e.target.value);
                  }}
                />
              </div>
              <div className="license-input">
                <p className="label">
                  {intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.dateOfBirth`,
                  })}
                </p>
                <DatePicker
                  format={ANT_PICKERS_FORMAT}
                  getPopupContainer={(triggerNode: HTMLElement) => {
                    return triggerNode.parentNode as HTMLElement;
                  }}
                  style={{ width: '100%' }}
                  value={dateOfBirth ? dayjs(dateOfBirth) : undefined}
                  placeholder={intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.dateOfBirth.placeholder`,
                  })}
                  onChange={(date) => setDateOfBirth(date.toISOString())}
                />
              </div>
              <div className="license-input">
                <p className="label">
                  {intl.formatMessage({
                    id: `fleetadmin.users.license.issuingAuthority.placeholder`,
                  })}
                </p>
                <Input
                  value={issuingAuthority}
                  placeholder={intl.formatMessage({
                    id: `fleetadmin.users.license.issuingAuthority.placeholder`,
                  })}
                  onChange={(e: any) => {
                    setIssuingAuthority(e.target.value);
                  }}
                />
              </div>
              <div className="license-input">
                <p className="label">
                  {intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.issueDate`,
                  })}
                </p>
                <DatePicker
                  format={ANT_PICKERS_FORMAT}
                  getPopupContainer={(triggerNode: HTMLElement) => {
                    return triggerNode.parentNode as HTMLElement;
                  }}
                  style={{ width: '100%' }}
                  value={issueDate ? dayjs(issueDate) : undefined}
                  placeholder={intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.issueDate.placeholder`,
                  })}
                  onChange={(date) => setIssueDate(date.toISOString())}
                />
              </div>
              <div className="license-input">
                <p className="label">
                  {intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.classB.From`,
                  })}
                </p>
                <DatePicker
                  format={ANT_PICKERS_FORMAT}
                  getPopupContainer={(triggerNode: HTMLElement) => {
                    return triggerNode.parentNode as HTMLElement;
                  }}
                  style={{ width: '100%' }}
                  value={classBValidFrom ? dayjs(classBValidFrom) : undefined}
                  placeholder={intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.classB.From.placeholder`,
                  })}
                  onChange={(date) => setClassBValidFrom(date.toISOString())}
                />
              </div>
              <div className="license-input">
                <p className="label">
                  {intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.classB.until`,
                  })}
                </p>
                <DatePicker
                  format={ANT_PICKERS_FORMAT}
                  getPopupContainer={(triggerNode: HTMLElement) => {
                    return triggerNode.parentNode as HTMLElement;
                  }}
                  style={{ width: '100%' }}
                  value={classBValidUntil ? dayjs(classBValidUntil) : undefined}
                  placeholder={intl.formatMessage({
                    id: `fleetadmin.users.info.license.manualForm.classB.until.placeholder`,
                  })}
                  onChange={(date) => setClassBValidUntil(date.toISOString())}
                />
              </div>
              <p className="label">
                {intl.formatMessage({
                  id: `fleetadmin.users.info.license.manualForm.issueCountry`,
                })}
              </p>
              <Select
                dropdownStyle={{ zIndex: zIndex.dropDown }}
                value={issueCountry}
                style={{ width: '100%' }}
                placeholder={intl.formatMessage({
                  id: `fleetadmin.users.info.id.manualForm.issueCountry`,
                })}
                onChange={(code: string) => setIssueCountry(code)}
              >
                {supportedLicenseCountries?.map((country) => (
                  <Option key={`${country}`} value={country}>
                    {intl.formatMessage({
                      id: `${country}`,
                    })}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
          <Button
            disabled={!(number && issueCountry && issueDate && classBValidFrom)}
            className="submit-button"
            onClick={handleOnSubmit}
          >
            {intl.formatMessage({
              id: `submit`,
            })}
          </Button>
          <Button className="cancel-button" onClick={handleOnCancel}>
            {intl.formatMessage({
              id: `cancel`,
            })}
          </Button>
        </div>
      </DrawerModal>
      <style jsx>{styles}</style>
    </>
  );
};

export default EditLicenseModal;
